import type { StudioItem, StudioPlacement, StudioRunGeometry } from "./studioTypes";

export interface WalkEntry {
  skuId: string;
  run: number;
  bay: number;
  shelf: number;
  xCm: number;
}

export interface BasePogWalk {
  order: readonly string[];
  rankOf: ReadonlyMap<string, number>;

  firstPlacement: ReadonlyMap<string, WalkEntry>;
}

const bayOf = (xCm: number, run: StudioRunGeometry | undefined): number => {
  if (!run || !(run.bayWidthCm > 0)) return 1;
  return Math.min(Math.max(1, Math.floor(xCm / run.bayWidthCm) + 1), Math.max(run.bays, 1));
};

// Authored bays survive merged-run reflow; derive only when the source omitted one.
export const placementBayOf = (
  placement: Pick<StudioPlacement, "bay" | "xCm">,
  run: StudioRunGeometry | undefined,
): number => placement.bay ?? bayOf(placement.xCm, run);

export const basePogWalk = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
): BasePogWalk => {
  const runOf = new Map(runs.map((run) => [run.run, run]));
  const entries: { key: readonly number[]; entry: WalkEntry }[] = [];
  const placeless: string[] = [];
  for (const item of items) {
    let best: { key: readonly number[]; entry: WalkEntry } | null = null;
    for (const placement of item.placements) {
      const bay = placementBayOf(placement, runOf.get(placement.run));
      const key = [placement.run, bay, placement.shelf, placement.xCm] as const;
      if (!best || compareKeys(key, best.key) < 0) {
        best = {
          key,
          entry: {
            skuId: item.skuId,
            run: placement.run,
            bay,
            shelf: placement.shelf,
            xCm: placement.xCm,
          },
        };
      }
    }
    if (best) entries.push(best);
    else placeless.push(item.skuId);
  }
  entries.sort((a, b) => compareKeys(a.key, b.key));
  // Unplaced items stay deterministic without pretending they have wall positions.
  const order = [...entries.map((entry) => entry.entry.skuId), ...placeless];
  return {
    order,
    rankOf: new Map(order.map((skuId, index) => [skuId, index])),
    firstPlacement: new Map(entries.map((entry) => [entry.entry.skuId, entry.entry])),
  };
};

/** The walk with cdt0s in the order the tree ships (unlisted ones after), order inside a cdt0
 *  unchanged: the merchant's tree, not the base pog, says where a section stands. */
export const walkOrderedByCdt0 = (
  walk: BasePogWalk,
  cdt0Of: (skuId: string) => string,
  cdt0Order: readonly string[],
): BasePogWalk => {
  if (cdt0Order.length === 0) return walk;
  const requestedRank = new Map<string, number>();
  cdt0Order.forEach((cdt0, index) => {
    if (!requestedRank.has(cdt0)) requestedRank.set(cdt0, index);
  });
  const firstRankOfCdt0 = new Map<string, number>();
  for (const skuId of walk.order) {
    const cdt0 = cdt0Of(skuId);
    if (!firstRankOfCdt0.has(cdt0)) firstRankOfCdt0.set(cdt0, walk.rankOf.get(skuId) ?? 0);
  }
  const order = walk.order
    .map((skuId) => {
      const cdt0 = cdt0Of(skuId);
      const requested = requestedRank.get(cdt0);
      return {
        skuId,
        key: [
          requested ?? cdt0Order.length + (firstRankOfCdt0.get(cdt0) ?? 0),
          walk.rankOf.get(skuId) ?? Number.MAX_SAFE_INTEGER,
        ],
      };
    })
    .sort((a, b) => compareKeys(a.key, b.key))
    .map(({ skuId }) => skuId);
  return {
    order,
    rankOf: new Map(order.map((skuId, index) => [skuId, index])),
    firstPlacement: walk.firstPlacement,
  };
};

const compareKeys = (a: readonly number[], b: readonly number[]): number => {
  for (let index = 0; index < Math.min(a.length, b.length); index += 1) {
    if (a[index] !== b[index]) return a[index] - b[index];
  }
  return 0;
};

export interface PairPriority {
  cdt0: string;
  cdt1: string;

  segment: string;
  priority: number;
}

export interface PairPriorityResult {
  priorityOf: ReadonlyMap<string, number>;
  pairs: readonly PairPriority[];

  cdt0Order: readonly string[];
}

const PAIR_KEY_SEPARATOR = "␟";

export const pairKeyOf = (cdt0: string, cdt1: string): string =>
  `${cdt0}${PAIR_KEY_SEPARATOR}${cdt1}`;

export const pairPriorities = (
  items: readonly StudioItem[],
  walk: BasePogWalk,
  cdt0Of: (item: StudioItem) => string,
  cdt1Of: (item: StudioItem) => string,
  cdt0Order: readonly string[] = [],
  parentOf: (item: StudioItem) => string = cdt0Of,
): PairPriorityResult => {
  const firstRankOfPair = new Map<string, number>();
  const firstRankOfCdt0 = new Map<string, number>();
  const segmentOf = new Map<string, string>();
  const pairCdt = new Map<string, { cdt0: string; cdt1: string }>();
  for (const item of items) {
    const rank = walk.rankOf.get(item.skuId) ?? Number.MAX_SAFE_INTEGER;
    const cdt0 = cdt0Of(item);
    const cdt1 = cdt1Of(item);
    const key = pairKeyOf(cdt0, cdt1);
    if (!firstRankOfPair.has(key) || rank < firstRankOfPair.get(key)!) {
      firstRankOfPair.set(key, rank);
    }
    if (!firstRankOfCdt0.has(cdt0) || rank < firstRankOfCdt0.get(cdt0)!) {
      firstRankOfCdt0.set(cdt0, rank);
    }
    if (!segmentOf.has(key)) segmentOf.set(key, `${parentOf(item)}/${cdt1}`);
    if (!pairCdt.has(key)) pairCdt.set(key, { cdt0, cdt1 });
  }

  const orderedCdt0s = [
    ...cdt0Order.filter((cdt0) => firstRankOfCdt0.has(cdt0)),
    ...[...firstRankOfCdt0.keys()]
      .filter((cdt0) => !cdt0Order.includes(cdt0))
      .sort((a, b) => firstRankOfCdt0.get(a)! - firstRankOfCdt0.get(b)!),
  ];

  const priorityOf = new Map<string, number>();
  const pairs: PairPriority[] = [];
  let next = 0;
  for (const cdt0 of orderedCdt0s) {
    const ownPairs = [...firstRankOfPair.entries()]
      .filter(([key]) => pairCdt.get(key)?.cdt0 === cdt0)
      .sort((a, b) => a[1] - b[1]);
    for (const [key] of ownPairs) {
      next += 1;
      priorityOf.set(key, next);
      const { cdt1 } = pairCdt.get(key)!;
      pairs.push({ cdt0, cdt1, segment: segmentOf.get(key) ?? cdt1, priority: next });
    }
  }
  return { priorityOf, pairs, cdt0Order: orderedCdt0s };
};
